import React, { useEffect, useMemo, useState } from 'react';
import { FilePdf, Eye, CircleNotch, Folder, FolderOpen, CaretRight, DownloadSimple } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { downloadDocument } from '../../utils/downloadDocument';

const API = `${window.location.origin}/api`;
const SPEC_ROOT = 'Specyfikacje DPD';
const MONTHS = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];

const isSpec = (d) => (d.folder_path || '').startsWith(SPEC_ROOT) || /specyfikacj/i.test(d.filename || '') || d.category === 'specyfikacje';

// Folder path segments under one of two roots: "Specyfikacje DPD" or "Inne".
const docSegments = (d) => {
    const fp = (d.folder_path || '').split('/').filter(Boolean);
    if (isSpec(d)) {
        if (fp[0] === SPEC_ROOT) return fp;
        const m = (d.filename || '').match(/(\d{4})-(\d{2})-\d{2}/);
        return m ? [SPEC_ROOT, m[1], m[2]] : [SPEC_ROOT, 'Nieokreślone'];
    }
    return ['Inne', ...fp];
};

const buildTree = (docs) => {
    const root = { children: {}, docs: [] };
    docs.forEach((d) => {
        let node = root;
        docSegments(d).forEach((seg) => {
            node.children[seg] = node.children[seg] || { children: {}, docs: [] };
            node = node.children[seg];
        });
        node.docs.push(d);
    });
    return root;
};

const countDocs = (n) => n.docs.length + Object.values(n.children).reduce((s, c) => s + countDocs(c), 0);

const sortKeys = (keys) => [...keys].sort((a, b) => {
    const na = Number(a), nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return nb - na;
    return a.localeCompare(b, 'pl');
});

const label = (name, parentName) => {
    const n = Number(name);
    if (/^\d{2}$/.test(name) && n >= 1 && n <= 12 && /^\d{4}$/.test(parentName || '')) return `${name} · ${MONTHS[n - 1]}`;
    return name;
};

function DocumentRow({ doc, onView }) {
    return (
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-border transition-colors" data-testid={`emp-doc-${doc.id}`}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <FilePdf className="w-5 h-5 text-red-600 shrink-0" />
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-slate-800 truncate">{doc.name || doc.filename}</div>
                    <div className="text-xs text-slate-500 truncate">
                        {doc.upload_date && new Date(doc.upload_date).toLocaleDateString('pl-PL')}
                        {doc.filename && doc.filename !== (doc.name || '') && ` · ${doc.filename}`}
                    </div>
                </div>
            </div>
            <div className="flex gap-1.5 shrink-0 ml-2">
                <button onClick={() => onView(doc.id)} className="btn-secondary flex items-center gap-1.5 text-xs sm:text-sm" data-testid={`emp-doc-view-${doc.id}`}>
                    <Eye className="w-4 h-4" /> <span className="hidden sm:inline">Pokaż</span>
                </button>
                <button onClick={() => downloadDocument(doc.id)} className="btn-secondary flex items-center gap-1.5 text-xs sm:text-sm" data-testid={`emp-doc-download-${doc.id}`}>
                    <DownloadSimple className="w-4 h-4" /> <span className="hidden sm:inline">Pobierz</span>
                </button>
            </div>
        </div>
    );
}

function FolderNode({ name, node, path, parentName, onView, depth }) {
    const [open, setOpen] = useState(depth < 2);
    const keys = sortKeys(Object.keys(node.children));
    const tid = path.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
    return (
        <div className={depth > 0 ? 'ml-3 sm:ml-5' : ''} data-testid={`emp-folder-${tid}`}>
            <button
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-100 text-left transition-colors"
                data-testid={`emp-folder-toggle-${tid}`}
            >
                <CaretRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} weight="bold" />
                {open ? <FolderOpen className="w-4 h-4 text-amber-500" weight="fill" /> : <Folder className="w-4 h-4 text-amber-500" weight="fill" />}
                <span className={`text-sm ${depth === 0 ? 'font-bold text-slate-800' : 'font-medium text-slate-700'}`}>{label(name, parentName)}</span>
                <span className="ml-auto text-xs text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{countDocs(node)}</span>
            </button>
            {open && (
                <div className="space-y-2 mt-1 mb-2">
                    {/* createElement avoids JSX self-recursion (breaks visual-edits babel plugin) */}
                    {keys.map((k) => React.createElement(FolderNode, {
                        key: k, name: k, node: node.children[k], path: `${path}/${k}`, parentName: name, onView, depth: depth + 1,
                    }))}
                    {node.docs.length > 0 && (
                        <div className="space-y-2 ml-3 sm:ml-5">
                            {node.docs.map((d) => <DocumentRow key={d.id} doc={d} onView={onView} />)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function EmployeeDocumentsTab({ onView }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/documents?category=inne`);
                if (!cancelled) setDocs(data || []);
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const tree = useMemo(() => buildTree(docs), [docs]);
    const roots = [SPEC_ROOT, 'Inne'].filter((k) => tree.children[k]);

    if (loading) {
        return <div className="text-center py-12"><CircleNotch className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>;
    }

    return (
        <div className="max-w-[1200px] mx-auto space-y-4" data-testid="emp-documents-container">
            <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Dokumenty</h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">Dokumenty udostępnione dla Ciebie przez administratora.</p>
            </div>
            {roots.length === 0 ? (
                <div className="text-center py-16 text-slate-500 bg-white rounded-lg border border-slate-200" data-testid="emp-docs-empty">
                    Brak dokumentów udostępnionych dla Ciebie.
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-slate-200 p-2 sm:p-3 space-y-1">
                    {roots.map((k) => (
                        <FolderNode key={k} name={k} node={tree.children[k]} path={k} onView={onView} depth={0} />
                    ))}
                </div>
            )}
        </div>
    );
}
